import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    ApptAddressOption,
    apptAddressGroupBy,
    CreateChancel,
    renderApptAddressGroup,
    SelectTypeAutocomplete,
    ValidationInput,
} from 'components';
import { ErrorText, FindLoad, FindSuccessItem, useModal } from 'utils';
import { clientActions, httpRequestsOnSuccessActions } from 'store';

const SET_ACTION = 'SET_CLIENT_SCHEDULING_ADDRESS';
const CLEAR_ACTION = 'CLEAR_CLIENT_SCHEDULING_ADDRESS';
const NAME_MAX_LENGTH = 100; // SetSchedulingAddressDTO.name @MaxLength

/** `${type}:${id}` — ids are only unique per source type (client address / contact / company). */
export const schedulingOptionKey = (type, id) => `${type}:${id}`;

/** GET /client/:id/scheduling-address `options` → SelectTypeAutocomplete / ApptAddressOption items. */
export const schedulingAddressOptions = (options) =>
    (options || [])
        .filter((option) => option?.address?.formattedAddress)
        .map((option) => ({
            id: schedulingOptionKey(option.type, option.id),
            refId: option.id,
            type: option.type,
            label: option.label || null,
            name: option.address.formattedAddress,
        }));

/**
 * Add / edit the client's default scheduling address: the address new appts pre-select.
 * Save → PATCH { type, refId, name? }. Saving an edit with the address cleared → DELETE.
 */
export const SchedulingAddressModal = () => {
    const dispatch = useDispatch();
    const { close } = useModal();
    const clientById = useSelector((state) => state.client.clientItemInfo);
    const schedulingAddress = useSelector((state) => state.client.clientSchedulingAddress);
    const selected = schedulingAddress?.selected;
    const list = useMemo(() => schedulingAddressOptions(schedulingAddress?.options), [schedulingAddress?.options]);

    const [addressKey, setAddressKey] = useState(selected ? schedulingOptionKey(selected.type, selected.refId) : '');
    const [name, setName] = useState(selected?.name || '');
    const [error, setError] = useState('');

    const setLoader = FindLoad(SET_ACTION);
    const clearLoader = FindLoad(CLEAR_ACTION);
    const setSuccess = FindSuccessItem(SET_ACTION);
    const clearSuccess = FindSuccessItem(CLEAR_ACTION);

    useEffect(() => {
        if (setSuccess || clearSuccess) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(SET_ACTION));
            dispatch(httpRequestsOnSuccessActions.removeSuccess(CLEAR_ACTION));
            close();
        }
    }, [setSuccess, clearSuccess]);

    const handleSave = () => {
        const option = list.find((item) => item.id === addressKey);
        if (!option) {
            if (selected) {
                dispatch(clientActions.clearSchedulingAddress(clientById?.id));
            } else {
                setError('address');
            }
            return;
        }
        const body = { type: option.type, refId: option.refId };
        if (name?.trim()) body.name = name.trim();
        dispatch(clientActions.setSchedulingAddress(clientById?.id, body));
    };

    return (
        <div style={{ width: 463 }}>
            <p className="modal-header-title">{selected ? 'Edit Scheduling Address' : 'Set Scheduling Address'}</p>
            <div className="modal-body-wrapper">
                <ValidationInput
                    variant={'outlined'}
                    onChange={(e) => setName(e.target.value)}
                    value={name}
                    type={'text'}
                    label={'Label'}
                    name="name"
                    Length={NAME_MAX_LENGTH}
                />
                <SelectTypeAutocomplete
                    title={'Select an address'}
                    name={'address'}
                    list={list}
                    defaultValue={addressKey}
                    handleSelect={(e) => {
                        setAddressKey(e.target.value);
                        error && setError('');
                    }}
                    renderValue={(option) => option?.name}
                    groupBy={apptAddressGroupBy}
                    renderGroup={renderApptAddressGroup}
                    customOptions={(option) => <ApptAddressOption option={option} />}
                    error={error}
                    typeError={`Address ${ErrorText.isRequiredSelect}`}
                />
            </div>
            <CreateChancel
                loader={!!setLoader.length || !!clearLoader.length}
                create={selected ? 'Save Changes' : 'Set Address'}
                chancel={'Cancel'}
                onCreate={handleSave}
                onClose={close}
                buttonWidth="224px"
            />
        </div>
    );
};
